import { notifications } from '@mantine/notifications';
import { UserType } from 'types/user';
import * as Sentry from '@sentry/react';
import { apiRequest } from './api';
import { confirmResetPassword } from './auth.cuboh';

interface ConsolidatedLoginCubohUser {
  id: string;
  access_token: string;
  refresh_token: string;
}

/** @see https://github.com/ChowNow/Hermosa/blob/main/docs/api/schemas/components/cuboh_login_response.json */
type ConsolidatedLoginResponse =
  | { chownow: UserType; cuboh: null }
  | { chownow: null; cuboh: ConsolidatedLoginCubohUser }
  | {
      chownow: UserType;
      cuboh: { id: string; access_token: null; refresh_token: null };
    };

export const consolidatedLogin = (username: string, password: string, uniqueKey?: string) =>
  apiRequest<ConsolidatedLoginResponse>({
    customHost: import.meta.env.VITE_ADMIN_API_HOST as string,
    path: 'login',
    params: { username, password, ...(uniqueKey && { unique_key: uniqueKey }) },
    trackErrorsInSentry: ({ status }) => status !== 400,
    method: 'POST',
  });

interface ChowNowMeResponse {
  data: UserType;
  metadata: Record<string, unknown>;
}

export const fetchMe = () =>
  apiRequest<ChowNowMeResponse>({
    customHost: import.meta.env.VITE_ADMIN_API_HOST as string,
    trackErrorsInSentry: ({ status }) => status !== 403,
    path: 'user/me',
  });

interface ChowNowLoginResponse {
  version: string;
  warnings: string[];
}

export const login = (email: string, password: string, uniqueKey: string) =>
  apiRequest<ChowNowLoginResponse>({
    path: 'admin/login',
    params: {
      email,
      password,
      unique_key: uniqueKey,
    },
    method: 'POST',
  });

export const CONSOLIDATED_RESET_CUBOH_RESET_PASSWORD = 'CONSOLIDATED_RESET_CUBOH_RESET_PASSWORD';

interface ConsolidatedPasswordResetResult {
  data: string | ConsolidatedLoginResponse | null;
  error: Error | null;
}

export const consolidatedPasswordReset = async (
  email: string,
  password: string,
  uniqueKey: string,
): Promise<ConsolidatedPasswordResetResult> => {
  // Try both password reset flows in parallel
  // Hub users: confirmResetPassword succeeds, login fails
  // ChowNow users: login succeeds, confirmResetPassword fails
  const [chowNowResult, cubohResult] = await Promise.allSettled([
    login(email, password, uniqueKey),
    confirmResetPassword(password, uniqueKey),
  ]);

  // Check if the API calls actually succeeded
  // login() returns { data, error }, so check both status and actual data
  const chowNowSucceeded =
    chowNowResult.status === 'fulfilled' && chowNowResult.value.data && !chowNowResult.value.error;
  // confirmResetPassword() returns void, so just check if promise fulfilled
  const cubohSucceeded = cubohResult.status === 'fulfilled';

  // Case 1: ChowNow user - login succeeded
  // The login() call already set the session cookie, proceed with consolidated login
  if (chowNowSucceeded) {
    const consolidatedResult = await consolidatedLogin(email, password);

    // consolidatedLogin returns { data, error } structure
    if (consolidatedResult.error) {
      return {
        data: null,
        error:
          consolidatedResult.error instanceof Error
            ? consolidatedResult.error
            : new Error('Failed to complete login'),
      };
    }

    return {
      data: consolidatedResult.data,
      error: null,
    };
  }

  // Case 2: Hub/Cuboh user - password reset succeeded
  if (cubohSucceeded) {
    notifications.show({
      title: 'Password Reset',
      message: 'Successfully reset password!',
    });

    return {
      data: CONSOLIDATED_RESET_CUBOH_RESET_PASSWORD,
      error: null,
    };
  }

  // Case 3: Both failed
  // Extract error messages from the results
  const chowNowError =
    chowNowResult.status === 'fulfilled' ? chowNowResult.value.error : chowNowResult.reason;

  const cubohError = cubohResult.status === 'rejected' ? cubohResult.reason : null;
  let cubohErrorMessage = '';
  let isPasswordError = false;

  // Parse Cuboh error response if available
  if (cubohError?.request?.response) {
    try {
      const responseData =
        typeof cubohError.request.response === 'string'
          ? JSON.parse(cubohError.request.response)
          : cubohError.request.response;

      // Extract password error message if present
      if (responseData?.password && Array.isArray(responseData.password)) {
        [cubohErrorMessage] = responseData.password;
        isPasswordError = true;
      } else if (responseData?.non_field_errors && Array.isArray(responseData.non_field_errors)) {
        [cubohErrorMessage] = responseData.non_field_errors;
      } else if (responseData?.detail) {
        cubohErrorMessage = responseData.detail;
      }
    } catch (parseError) {
      Sentry.captureException(
        parseError instanceof Error ? parseError : new Error('Error parsing Cuboh error response'),
      );
    }
  }

  // Return the most informative error message
  const errorMessage =
    chowNowError?.message || cubohErrorMessage || cubohError?.message || 'Failed to reset password';

  const error = new Error(errorMessage) as Error & { field?: string };
  // Add a custom property to indicate this is a password field error
  if (isPasswordError) {
    error.field = 'password';
  }

  return {
    data: null,
    error,
  };
};

export const resetPassword = async (email: string, redirectUrl: string, activateAccount: boolean) =>
  apiRequest({
    path: 'admin/reset-password',
    params: {
      email,
      redirect_url: redirectUrl || '',
      activate_account: activateAccount,
    },
    method: 'POST',
  });
